/**
 * FrameworkApplicabilityPanel Component
 *
 * Displays applicable compliance/regulatory frameworks for the organization,
 * a hero gauge showing aggregate assessment coverage, and per-framework
 * cards with coverage bars and advisory context.
 */

import { useState, useMemo, useCallback } from 'react'
import { getApplicableFrameworks, getAggregateCoverage } from '../../../shared/utils/frameworkApplicability'
import { getOrgCharacteristics, getFrameworkOverlays } from '../../../shared/utils/orgCharacteristics'
import { supabase } from '../../../shared/lib/supabase'
import frameworkCatalog from '../../../shared/data/frameworkCatalog.json'
import ComplianceGauge from './ComplianceGauge'

const ALL_FRAMEWORKS = Object.entries(frameworkCatalog.frameworks).map(([id, fw]) => ({ id, name: fw.name, fullName: fw.fullName, category: fw.category }))

function FrameworkApplicabilityPanel({ profile, responses, client }) {
  const [expandedFramework, setExpandedFramework] = useState(null)
  const [showFrameworkSelector, setShowFrameworkSelector] = useState(false)
  const [adminFrameworks, setAdminFrameworks] = useState(client?.additional_frameworks || [])
  const [saving, setSaving] = useState(false)

  const orgCharacteristics = useMemo(() =>
    getOrgCharacteristics(responses?.businessScoping),
    [responses?.businessScoping]
  )

  const frameworkOverlays = useMemo(() =>
    getFrameworkOverlays(responses?.businessScoping),
    [responses?.businessScoping]
  )

  const applicableFrameworks = useMemo(() => {
    if (!profile) return []
    return getApplicableFrameworks(
      profile.verticalKey,
      profile.regulatoryContext?.frameworks || [],
      orgCharacteristics.selectedKeys || [],
      frameworkOverlays.selectedKeys || [],
      adminFrameworks
    )
  }, [profile, orgCharacteristics.selectedKeys, frameworkOverlays.selectedKeys, adminFrameworks])

  const saveAdminFrameworks = useCallback(async (newFrameworks) => {
    setAdminFrameworks(newFrameworks)
    if (!client?.id) return
    setSaving(true)
    try {
      await supabase
        .from('clients')
        .update({ additional_frameworks: newFrameworks })
        .eq('id', client.id)
    } catch (err) {
      console.error('Failed to save additional frameworks:', err)
    } finally {
      setSaving(false)
    }
  }, [client?.id])

  const toggleAdminFramework = useCallback((fwId) => {
    const newFrameworks = adminFrameworks.includes(fwId)
      ? adminFrameworks.filter(id => id !== fwId)
      : [...adminFrameworks, fwId]
    saveAdminFrameworks(newFrameworks)
  }, [adminFrameworks, saveAdminFrameworks])

  const aggregateCoverage = useMemo(() =>
    getAggregateCoverage(applicableFrameworks),
    [applicableFrameworks]
  )

  if (!profile || applicableFrameworks.length === 0) return null

  // Sort: regulatory first, then frameworks, then by coverage ascending
  const sorted = [...applicableFrameworks].sort((a, b) => {
    const catOrder = { regulatory: 0, industry: 1, framework: 2 }
    const catDiff = (catOrder[a.category] ?? 3) - (catOrder[b.category] ?? 3)
    if (catDiff !== 0) return catDiff
    return (a.coverage?.percentage || 0) - (b.coverage?.percentage || 0)
  })

  return (
    <div className="card p-6 mt-6">
      {/* Header */}
      <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Compliance Program Scope
        </h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
          Applicable frameworks based on industry profile, organizational characteristics, and compliance selections
        </p>
      </div>

      {/* Hero Gauge + Summary */}
      <div className="flex flex-col md:flex-row items-center gap-6 mb-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <ComplianceGauge
          percentage={aggregateCoverage.avgCoverage}
          label="Avg. Domain Coverage"
          size={180}
        />
        <div className="flex-1 text-center md:text-left">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
            Our maturity assessment evaluates <span className="font-bold text-gray-900 dark:text-white">{aggregateCoverage.domainsReferenced} of {aggregateCoverage.totalDomains}</span> security domains
            relevant to your <span className="font-bold text-gray-900 dark:text-white">{aggregateCoverage.frameworkCount}</span> in-scope frameworks.
          </p>
          <div className="flex flex-wrap gap-2 justify-center md:justify-start">
            <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
              {sorted.filter(f => f.category === 'regulatory').length} Regulatory
            </span>
            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              {sorted.filter(f => f.category === 'industry').length} Industry
            </span>
            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
              {sorted.filter(f => f.category === 'framework').length} Voluntary
            </span>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 italic">
            Full GRC program implementation is recommended for complete compliance coverage.
          </p>
        </div>
      </div>

      {/* Admin Framework Selector */}
      {client && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Additional Frameworks
            </p>
            <button
              onClick={() => setShowFrameworkSelector(!showFrameworkSelector)}
              className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
            >
              {showFrameworkSelector ? 'Done' : '+ Add Frameworks'}
            </button>
          </div>

          {/* Selected admin frameworks as removable pills */}
          {adminFrameworks.length > 0 && !showFrameworkSelector && (
            <div className="flex flex-wrap gap-1.5">
              {adminFrameworks.map(fwId => {
                const fw = ALL_FRAMEWORKS.find(f => f.id === fwId)
                return (
                  <span key={fwId} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                    {fw?.name || fwId}
                    <button
                      onClick={() => toggleAdminFramework(fwId)}
                      className="hover:text-red-500 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </span>
                )
              })}
              {saving && <span className="text-xs text-gray-400 dark:text-gray-500 italic">Saving...</span>}
            </div>
          )}

          {/* Framework selector grid */}
          {showFrameworkSelector && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Select additional frameworks to include in the compliance scope. Regulatory and industry frameworks are auto-detected from the client's profile.
              </p>
              <div className="flex flex-wrap gap-2">
                {ALL_FRAMEWORKS
                  .filter(fw => {
                    // Don't show frameworks already included by auto-detection
                    const autoIncluded = applicableFrameworks.find(af => af.id === fw.id && !adminFrameworks.includes(fw.id))
                    return !autoIncluded
                  })
                  .map(fw => {
                    const isSelected = adminFrameworks.includes(fw.id)
                    return (
                      <button
                        key={fw.id}
                        type="button"
                        onClick={() => toggleAdminFramework(fw.id)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                        title={fw.fullName}
                      >
                        {fw.name}
                        {isSelected && (
                          <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                        )}
                      </button>
                    )
                  })}
              </div>
              {saving && <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 italic">Saving...</p>}
            </div>
          )}
        </div>
      )}

      {/* Framework Cards */}
      <div className="space-y-3">
        {sorted.map(fw => {
          const isExpanded = expandedFramework === fw.id
          const coveragePct = fw.coverage?.percentage || 0
          const barColor = coveragePct < 40
            ? 'bg-red-500 dark:bg-red-400'
            : coveragePct < 70
            ? 'bg-amber-500 dark:bg-amber-400'
            : 'bg-green-500 dark:bg-green-400'
          const categoryColor = fw.category === 'regulatory'
            ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
            : fw.category === 'industry'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'

          return (
            <div key={fw.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* Card Header — clickable */}
              <button
                onClick={() => setExpandedFramework(isExpanded ? null : fw.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-white text-sm">
                        {fw.name}
                      </p>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${categoryColor}`}>
                        {fw.category === 'regulatory' ? 'Regulatory' : fw.category === 'industry' ? 'Industry' : 'Framework'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{fw.fullName}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                  {/* Coverage bar */}
                  <div className="hidden sm:flex items-center gap-2 w-32">
                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${barColor}`}
                        style={{ width: `${coveragePct}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right">{coveragePct}%</span>
                  </div>

                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700/50">
                  <div className="pt-3 space-y-4">
                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {fw.description}
                    </p>

                    {/* Why it applies */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Why This Applies
                      </p>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                        {fw.reasons.map((reason, i) => (
                          <li key={i} className="flex items-center gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-indigo-400 flex-shrink-0"></span>
                            {reason}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Key Requirements */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                        Key Requirement Areas
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {fw.keyRequirements.map((req, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded">
                            {req}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Assessment Domain Coverage */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                        Assessment Domain Coverage ({fw.coverage.coveredCount}/{fw.coverage.totalDomains} domains)
                      </p>
                      {/* Mobile coverage bar */}
                      <div className="sm:hidden flex items-center gap-2 mb-2">
                        <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${barColor}`}
                            style={{ width: `${coveragePct}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{coveragePct}%</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {fw.coverage.coveredDomains.map(d => (
                          <span key={d.id} className="text-xs px-2 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded">
                            {d.name}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Regulatory body */}
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Oversight: {fw.regulatoryBody}
                    </p>

                    {/* Next Steps */}
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/50">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        Next Steps
                      </p>
                      <div className={`p-3 rounded-lg text-sm ${
                        coveragePct < 40
                          ? 'bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800/50'
                          : coveragePct < 70
                          ? 'bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/50'
                          : 'bg-green-50 dark:bg-green-900/15 border border-green-200 dark:border-green-800/50'
                      }`}>
                        <p className={`text-sm font-medium mb-1.5 ${
                          coveragePct < 40 ? 'text-blue-800 dark:text-blue-300'
                          : coveragePct < 70 ? 'text-amber-800 dark:text-amber-300'
                          : 'text-green-800 dark:text-green-300'
                        }`}>
                          {coveragePct < 40
                            ? 'Limited assessment coverage — further exploration recommended'
                            : coveragePct < 70
                            ? 'Moderate assessment coverage — targeted analysis recommended'
                            : 'Strong assessment coverage — maintain and refine'
                          }
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                          {coveragePct < 40 ? (
                            fw.category === 'regulatory'
                              ? `Our maturity assessment evaluates ${fw.coverage.coveredCount} of ${fw.coverage.totalDomains} security domains relevant to ${fw.name}, providing visibility into ${coveragePct}% of the framework's scope. The remaining domains fall outside this assessment's coverage and require a dedicated ${fw.name} compliance analysis to determine the organization's full regulatory posture. We recommend a focused ${fw.name} readiness assessment or engaging a GRC program to evaluate the uncovered requirement areas.`
                              : `Our maturity assessment evaluates ${fw.coverage.coveredCount} of ${fw.coverage.totalDomains} security domains mapped to ${fw.name}, covering ${coveragePct}% of the framework's scope. The domains assessed provide a baseline view of alignment, but a comprehensive evaluation against ${fw.name}'s full control set is recommended to determine complete readiness. Consider incorporating the uncovered domains into a future assessment cycle or dedicated review.`
                          ) : coveragePct < 70 ? (
                            fw.category === 'regulatory'
                              ? `Our maturity assessment evaluates ${fw.coverage.coveredCount} of ${fw.coverage.totalDomains} security domains relevant to ${fw.name}, covering ${coveragePct}% of the framework's scope. The assessed domains provide meaningful insight into the organization's compliance posture, though full ${fw.name} compliance determination requires evaluating the remaining domains. We recommend a targeted analysis of the uncovered requirement areas, prioritizing those with the highest regulatory impact.`
                              : `Our maturity assessment evaluates ${fw.coverage.coveredCount} of ${fw.coverage.totalDomains} domains mapped to ${fw.name}, covering ${coveragePct}% of the framework's scope. This provides a solid foundation for understanding alignment. To achieve full ${fw.name} readiness, consider extending coverage to the remaining domains through a focused review or GRC program integration.`
                          ) : (
                            fw.category === 'regulatory'
                              ? `Our maturity assessment evaluates ${fw.coverage.coveredCount} of ${fw.coverage.totalDomains} domains relevant to ${fw.name}, providing ${coveragePct}% coverage of the framework's scope. This strong coverage enables a high-confidence view of the organization's compliance posture. Continue to maintain current controls, monitor for regulatory updates, and consider formal audit readiness testing against ${fw.name} requirements for the remaining domains.`
                              : `Our maturity assessment evaluates ${fw.coverage.coveredCount} of ${fw.coverage.totalDomains} domains mapped to ${fw.name}, covering ${coveragePct}% of the framework's scope. This reflects strong alignment with ${fw.name} expectations. Continue refining existing controls, document evidence of compliance, and consider pursuing formal certification or attestation if applicable.`
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Key Recommendations */}
      {sorted.length > 0 && (
        <div className="card p-6 mt-6">
          <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Recommended Next Steps</h3>
          </div>
          <div className="space-y-3">
            {(() => {
              const recs = []
              sorted.forEach(fw => {
                const cov = fw.coverage
                if (!cov) return
                const pct = cov.totalDomains > 0 ? Math.round((cov.coveredCount / cov.totalDomains) * 100) : 0

                if (fw.name === 'HIPAA' || fw.fullName?.includes('HIPAA')) {
                  if (pct < 60) recs.push({ priority: 'high', text: `Conduct a formal HIPAA Security Rule risk assessment to validate full compliance posture (current assessment coverage: ${pct}%)` })
                }
                if (fw.name === 'PCI DSS' || fw.fullName?.includes('PCI')) {
                  if (pct < 60) recs.push({ priority: 'high', text: `Complete PCI DSS SAQ self-assessment for payment card infrastructure (current assessment coverage: ${pct}%)` })
                }
                if (fw.name === 'GLBA' || fw.fullName?.includes('GLBA') || fw.fullName?.includes('Gramm-Leach')) {
                  if (pct < 50) recs.push({ priority: 'medium', text: `Conduct a focused GLBA Safeguards Rule review to evaluate coverage beyond this assessment's scope (current assessment coverage: ${pct}%)` })
                }
                if (fw.name === 'CIS Controls' || fw.fullName?.includes('CIS')) {
                  if (pct < 50) recs.push({ priority: 'medium', text: `Map CIS Controls Implementation Group 1 gaps to the enablement plan for foundational security hygiene` })
                }
              })

              if (sorted.some(fw => {
                const pct = fw.coverage?.totalDomains > 0 ? Math.round((fw.coverage.coveredCount / fw.coverage.totalDomains) * 100) : 100
                return pct < 40
              })) {
                recs.push({ priority: 'medium', text: 'Prioritize domain remediation for frameworks with assessment coverage below 40%' })
              }

              if (recs.length === 0) {
                recs.push({ priority: 'low', text: 'Continue monitoring regulatory changes and maintain current maturity levels across all applicable frameworks' })
              }

              return recs.slice(0, 4).map((rec, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 ${
                    rec.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                    : rec.priority === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  }`}>
                    {idx + 1}
                  </span>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{rec.text}</p>
                </div>
              ))
            })()}
          </div>
        </div>
      )}
    </div>
  )
}

export default FrameworkApplicabilityPanel
